import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { PaymentButton, PaymentSecurityBadge } from './PaymentComponents';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';

// Icons
import { 
  PlayCircle, 
  Calendar, 
  Clock, 
  DollarSign, 
  Users, 
  Radio,
  ShoppingCart,
  Lock,
  Unlock,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Eye,
  MessageCircle
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// Create Live Stream Dialog Component
export const CreateLiveStreamDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    access_type: 'purchase',
    scheduled_time: ''
  });
  const [loading, setLoading] = useState(false);

  // Set default scheduled time to 1 hour from now
  useEffect(() => {
    if (open && !formData.scheduled_time) {
      const now = new Date();
      now.setHours(now.getHours() + 1);
      setFormData(prev => ({
        ...prev,
        scheduled_time: now.toISOString().slice(0, 16)
      }));
    }
  }, [open, formData.scheduled_time]);

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.price || !formData.scheduled_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        scheduled_time: new Date(formData.scheduled_time).toISOString()
      };

      await axios.post(`${API}/streams`, submitData, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      toast.success('Live stream created successfully!');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        price: '',
        access_type: 'purchase',
        scheduled_time: ''
      });
    } catch (error) {
      console.error('Error creating live stream:', error);
      toast.error('Failed to create live stream');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <PlayCircle className="h-5 w-5 text-red-600" />
            <span>Create Live Stream</span>
          </DialogTitle>
          <DialogDescription>
            Schedule a premium live workout session that users can purchase access to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Stream Title *</label>
            <Input
              placeholder="e.g., Morning HIIT Workout Session"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Description *</label>
            <Textarea
              placeholder="Describe your live workout session, what equipment is needed, and what participants can expect..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Access Price ($) *</label>
              <Input
                type="number"
                placeholder="9.99"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Scheduled Time *</label>
              <Input
                type="datetime-local"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Access Type</label>
            <Select value={formData.access_type} onValueChange={(value) => setFormData({ ...formData, access_type: value })}>
              <SelectTrigger data-testid="stream-access-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public — Free for everyone</SelectItem>
                <SelectItem value="subscription">Subscribers Only — Requires active subscription</SelectItem>
                <SelectItem value="purchase">One-Time Purchase — Pay to access</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {formData.access_type === 'public' && 'Anyone can view this stream for free.'}
              {formData.access_type === 'subscription' && 'Only your subscribers can access this stream.'}
              {formData.access_type === 'purchase' && 'Users must purchase access to this stream.'}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Radio className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Live Stream Tips:</p>
                <ul className="mt-1 text-blue-700 space-y-1">
                  <li>• Test your equipment before going live</li>
                  <li>• Have good lighting and clear audio</li>
                  <li>• Interact with your audience during the session</li>
                  <li>• Keep the energy high and provide clear instructions</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white"
            >
              {loading ? 'Creating...' : 'Schedule Stream'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Live Streams Tab Component
export const LiveStreamsTab = ({ liveStreams }) => {
  const [purchasingStream, setPurchasingStream] = useState(null);
  const [selectedStream, setSelectedStream] = useState(null);
  const [streamAccess, setStreamAccess] = useState({});

  const purchaseStreamAccess = async (streamId, price) => {
    setPurchasingStream(streamId);
    try {
      const sessionToken = Cookies.get('session_token');
      await axios.post(`${API}/streams/${streamId}/purchase`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Update access status
      setStreamAccess(prev => ({ ...prev, [streamId]: true }));
      toast.success('Stream access purchased successfully!');
    } catch (error) {
      console.error('Error purchasing stream access:', error);
      if (error.response?.status === 400) {
        toast.error('You already have access to this stream');
      } else {
        toast.error('Failed to purchase stream access');
      }
    } finally {
      setPurchasingStream(null);
    }
  };

  const checkStreamAccess = async (streamId) => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/streams/${streamId}/access`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      return response.data.has_access;
    } catch (error) {
      return false;
    }
  };

  // Check access for all streams
  useEffect(() => {
    const checkAllAccess = async () => {
      const accessPromises = liveStreams.map(async (stream) => {
        const hasAccess = await checkStreamAccess(stream.id);
        return { streamId: stream.id, hasAccess };
      });
      
      const accessResults = await Promise.all(accessPromises);
      const accessMap = {};
      accessResults.forEach(({ streamId, hasAccess }) => {
        accessMap[streamId] = hasAccess;
      });
      setStreamAccess(accessMap);
    };

    if (liveStreams.length > 0) {
      checkAllAccess();
    }
  }, [liveStreams]);

  const isStreamLive = (scheduledTime) => {
    const now = new Date();
    const streamTime = new Date(scheduledTime);
    const timeDiff = streamTime.getTime() - now.getTime();
    // Consider stream "live" if it's within 15 minutes of scheduled time
    return timeDiff <= 15 * 60 * 1000 && timeDiff >= -60 * 60 * 1000;
  };

  const getStreamStatus = (scheduledTime) => {
    const now = new Date();
    const streamTime = new Date(scheduledTime);
    
    if (isStreamLive(scheduledTime)) {
      return { label: 'LIVE', color: 'bg-red-500 text-white', icon: Radio };
    } else if (streamTime > now) {
      return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Calendar };
    } else {
      return { label: 'Ended', color: 'bg-gray-100 text-gray-700', icon: Clock };
    }
  };

  if (liveStreams.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <PlayCircle className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No live streams scheduled</h3>
        <p className="text-gray-600">Check back later for live workout sessions!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {liveStreams.map((stream) => {
          const status = getStreamStatus(stream.scheduled_time);
          const hasAccess = streamAccess[stream.id];
          const StatusIcon = status.icon;
          const isLocked = stream.is_locked;
          const accessType = stream.access_type || 'purchase';
          const description = stream.description || stream.description_preview || '';
          
          return (
            <Card key={stream.id} className={`group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${isLocked ? 'border-gray-200 opacity-95' : 'border-red-100'}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
                    <Badge className={`${status.color} px-2 py-1 animate-pulse`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                    {accessType === 'subscription' && (
                      <Badge data-testid={`stream-access-badge-${stream.id}`} className="bg-amber-100 text-amber-700 px-2 py-1">
                        <Lock className="h-3 w-3 mr-1" />
                        Subscribers
                      </Badge>
                    )}
                    {accessType === 'purchase' && (
                      <Badge data-testid={`stream-access-badge-${stream.id}`} className="bg-blue-100 text-blue-700 px-2 py-1">
                        <DollarSign className="h-3 w-3 mr-1" />
                        Purchase
                      </Badge>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {accessType !== 'public' && <div className="text-xl font-bold text-red-600 whitespace-nowrap">${stream.price}</div>}
                    {accessType === 'public' && <div className="text-base font-bold text-green-600">Free</div>}
                    <div className="text-xs text-gray-500 whitespace-nowrap">per access</div>
                  </div>
                </div>
                
                <CardTitle className="text-lg line-clamp-2">{stream.title}</CardTitle>
                
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(stream.scheduled_time).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>{new Date(stream.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-700 text-sm line-clamp-3">{description}</p>
                  
                  {!isLocked && (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-1 text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>{stream.viewers_count || 0} viewers</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      by {stream.creator_name}
                    </div>
                  </div>
                  )}

                  {isLocked && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <Lock className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-500">{stream.lock_message || 'Full details locked'}</span>
                    </div>
                  )}
                  
                  {/* CTA based on access type */}
                  {!isLocked && (hasAccess || accessType === 'public') ? (
                    <Button
                      onClick={() => setSelectedStream(stream)}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white"
                      disabled={!isStreamLive(stream.scheduled_time)}
                    >
                      {isStreamLive(stream.scheduled_time) ? (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Join Live Stream
                        </>
                      ) : accessType === 'public' ? (
                        <>
                          <Unlock className="h-4 w-4 mr-2" />
                          Free Access
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Access Purchased
                        </>
                      )}
                    </Button>
                  ) : isLocked && accessType === 'subscription' ? (
                    <Button data-testid={`stream-subscribe-cta-${stream.id}`} className="w-full bg-amber-500 hover:bg-amber-600 text-white" disabled>
                      <Lock className="h-4 w-4 mr-2" />
                      Subscribe to Access
                    </Button>
                  ) : isLocked && accessType === 'purchase' ? (
                    <PaymentButton
                      itemType="live_stream"
                      itemId={stream.id}
                      price={stream.price}
                      title={stream.title}
                      description={description}
                      className="w-full"
                    >
                      Purchase Access ${stream.price}
                    </PaymentButton>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Live Stream Viewer Dialog */}
      <LiveStreamViewer
        stream={selectedStream}
        open={!!selectedStream}
        onClose={() => setSelectedStream(null)}
      />
    </div>
  );
};

// Live Stream Viewer Component
const LiveStreamViewer = ({ stream, open, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!stream) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0">
        <div className="flex flex-col h-[80vh]">
          {/* Video Player Area */}
          <div className="relative flex-1 bg-black rounded-t-lg overflow-hidden">
            {/* Mock Video Player */}
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
              <div className="text-center text-white space-y-4">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto animate-pulse">
                  <Radio className="h-10 w-10" />
                </div>
                <h3 className="text-2xl font-bold">{stream.title}</h3>
                <p className="text-gray-300">Live Workout Session</p>
                <Badge className="bg-red-500 text-white px-3 py-1">
                  <Radio className="h-3 w-3 mr-1" />
                  LIVE
                </Badge>
              </div>
            </div>

            {/* Video Controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center space-x-4">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="text-white hover:bg-white/20"
                  >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsMuted(!isMuted)}
                    className="text-white hover:bg-white/20"
                  >
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                  
                  <div className="flex items-center space-x-2 text-sm">
                    <Eye className="h-4 w-4" />
                    <span>{stream.viewers_count + Math.floor(Math.random() * 50)} viewers</span>
                  </div>
                </div>
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="text-white hover:bg-white/20"
                >
                  <Maximize className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Stream Info & Chat */}
          <div className="p-6 border-t bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Stream Info */}
              <div className="md:col-span-2 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{stream.title}</h2>
                    <p className="text-gray-600 mt-1">{stream.description}</p>
                  </div>
                  <Badge className="bg-red-100 text-red-700">
                    Live Now
                  </Badge>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      {stream.creator_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-gray-900">{stream.creator_name}</p>
                    <p className="text-sm text-gray-600">Fitness Instructor</p>
                  </div>
                </div>
              </div>

              {/* Live Chat Mock */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <MessageCircle className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-900">Live Chat</span>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-600">
                      A
                    </div>
                    <div>
                      <p className="font-medium">Alex</p>
                      <p className="text-gray-600">Great workout! 💪</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-xs font-medium text-green-600">
                      S
                    </div>
                    <div>
                      <p className="font-medium">Sarah</p>
                      <p className="text-gray-600">Can you slow down the burpees?</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-medium text-purple-600">
                      M
                    </div>
                    <div>
                      <p className="font-medium">Mike</p>
                      <p className="text-gray-600">This is intense! 🔥</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <Input placeholder="Type a message..." className="text-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};